import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SIZE_MAP = {
  xs: { box: 'w-6 h-6',  txt: 'text-[10px]' },
  sm: { box: 'w-8 h-8',  txt: 'text-xs' },
  md: { box: 'w-12 h-12', txt: 'text-sm' },
  lg: { box: 'w-20 h-20', txt: 'text-lg' },
  xl: { box: 'w-32 h-32', txt: 'text-2xl' },
};

const INITIALS_BG = [
  'bg-emerald-100 text-emerald-800',
  'bg-sky-100 text-sky-800',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-800',
  'bg-violet-100 text-violet-800',
  'bg-teal-100 text-teal-800',
  'bg-indigo-100 text-indigo-800',
];

const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0] || '').join('').toUpperCase() || '?';
};

const colorFor = (name) => {
  if (!name) return INITIALS_BG[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return INITIALS_BG[h % INITIALS_BG.length];
};

// Tiny in-memory cache so the same photo isn't refetched across rows
// (e.g. when the roster re-renders). Keyed by absolute URL.
const blobCache = new Map();

const resolveAbsolute = (photoUrl) => {
  if (!photoUrl) return null;
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${BACKEND_URL}${photoUrl}`;
};

const fetchAuthedBlob = async (absUrl) => {
  if (blobCache.has(absUrl)) return blobCache.get(absUrl);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const resp = await fetch(absUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    const err = new Error(`Photo fetch failed (${resp.status})`);
    err.status = resp.status;
    throw err;
  }
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  blobCache.set(absUrl, objectUrl);
  return objectUrl;
};

/**
 * Reusable, square-circle athlete avatar.
 *  - Prefers `thumbUrl` (256x256) for list views, falls back to `photoUrl`.
 *  - Photos are fetched authenticated and rendered via a blob URL so the
 *    backend RBAC serve endpoint stays strict (no token leaks in query strings).
 *  - Falls back to colored initials when missing or on any fetch error.
 */
export const AthleteAvatar = ({
  photoUrl,
  thumbUrl,
  name,
  size = 'md',
  showName = false,
  subline,
  className = '',
  testId,
}) => {
  const sz = SIZE_MAP[size] || SIZE_MAP.md;
  const ringCls = 'ring-1 ring-gray-200 dark:ring-gray-700';
  const preferThumb = size === 'xs' || size === 'sm' || size === 'md';
  const chosen = (preferThumb && thumbUrl) ? thumbUrl : (photoUrl || thumbUrl);
  const absUrl = resolveAbsolute(chosen);

  const [src, setSrc] = useState(() => (absUrl && blobCache.get(absUrl)) || null);
  const [err, setErr] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    setErr(false);
    if (!absUrl) { setSrc(null); return; }
    const cached = blobCache.get(absUrl);
    if (cached) { setSrc(cached); return; }
    setSrc(null);
    fetchAuthedBlob(absUrl)
      .then(url => { if (aliveRef.current) setSrc(url); })
      .catch(() => { if (aliveRef.current) setErr(true); });
    return () => { aliveRef.current = false; };
  }, [absUrl]);

  const showImg = !err && src;

  const avatar = showImg ? (
    <img
      src={src}
      alt={`${name || 'Athlete'} profile photo`}
      loading="lazy"
      onError={() => setErr(true)}
      className={`${sz.box} rounded-full object-cover ${ringCls}`}
      data-testid={testId || 'athlete-avatar-img'}
    />
  ) : (
    <div
      className={`${sz.box} rounded-full flex items-center justify-center font-semibold ${colorFor(name)} ${sz.txt} ${ringCls} dark:bg-gray-700 dark:text-gray-200`}
      aria-label={`${name || 'Unknown'} initials`}
      data-testid={testId || 'athlete-avatar-initials'}
    >
      {initialsOf(name)}
    </div>
  );

  if (!showName) return <div className={className}>{avatar}</div>;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {avatar}
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{name || '—'}</p>
        {subline && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subline}</p>}
      </div>
    </div>
  );
};

export default AthleteAvatar;
